import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { ColorTwister } from 'components/productdetail/ColorTwister/ColorTwister';
import { cn } from 'helpers/classnames';
import SwankyStyleSwatch from 'components/productdetail/swankyswatch/SwankyStyleSwatch';
import { track } from 'apis/amethyst';
import { showAirplaneSizing } from 'helpers/AirplaneSeatSizing';
import { isColorOosForSelectedSizing } from 'helpers/SwankySwatchUtils';
import useMartyContext from 'hooks/useMartyContext';
import type { FormattedProductBundle, ProductDetailState } from 'reducers/detail/productDetail';
import type { AppState } from 'types/app';
import type { ProductStyle } from 'types/cloudCatalog';
import type { AirplaneCache } from 'types/AirplaneCache';
import { evColorTwister } from 'events/product';

import css from 'styles/components/productdetail/swankyswatch/SwankySwatchBox.scss';

type OnChange<T> = (e: React.ChangeEvent<T>) => void;

const INITIAL_COLLAPSED_STYLES = 12;
const COLOR_TWISTER_COLUMN_COUNT = 5;

export interface GivenProps {
  isGiftCard: boolean;
  onColorChange: (styleId: string, colorName?: string, isNew?: string) => void;
  productState: ProductDetailState;
  airplaneCache?: AirplaneCache;
  productType: string;
  selectedStyle: ProductStyle;
  styleList: ProductStyle[];
  collapsable?: boolean;
  sizingChooser?: React.ReactNode;
  pageType: string;
  shouldCheckStock?: boolean;
}

export type SwankySwatchBoxProps = GivenProps & ReturnType<typeof mapStateToProps> & React.ComponentProps<typeof ColorTwister.Root>;

function SwankySwatchBoxTreatment(props: SwankySwatchBoxProps) {
  const { collapsable = false, isGiftCard, onColorChange, selectedStyle, styleList, airplaneCache, sizingChooser, pageType, initialMode } = props;
  const { styleId: selectedStyleId } = selectedStyle;

  const onColorChangeForSwatches: OnChange<HTMLInputElement> = useCallback(
    e => {
      const {
        dataset: { colorName, styleId, isNew }
      } = e.target as HTMLInputElement;
      styleId && colorName && onColorChange(styleId, colorName, isNew);
    },
    [onColorChange]
  );

  const onColorChangeForDropdown: OnChange<HTMLSelectElement> = useCallback(
    e => {
      const { value } = e.target as HTMLSelectElement;
      if (value) {
        const newSelectedStyle = styleList.find(style => style.styleId === value);
        if (newSelectedStyle) {
          onColorChange(value, newSelectedStyle.color, newSelectedStyle.isNew);
        }
      }
    },
    [onColorChange, styleList]
  );

  let styleLabel = selectedStyle.specs ? 'Specs' : 'Color';
  let styleNamePrefix = '';
  if (isGiftCard) {
    styleLabel = 'Gift Amount';
    styleNamePrefix = '$';
  }

  const { testId } = useMartyContext();
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapse = () => setCollapsed(!collapsed);

  const totalStyleCount = styleList.length;
  const visibleStyleCount = Math.min(COLOR_TWISTER_COLUMN_COUNT, totalStyleCount);

  return (
    <ColorTwister.Root initialMode={initialMode}>
      <div className={cn({ [css.isCollapsed]: collapsable && collapsed })}>
        <div className="mb-2 flex items-baseline justify-between gap-6">
          <div>
            <span className={cn(css.colorTextLabel, 'lumon:body-s-bold')}>{styleLabel}:</span>
            <span className={cn(css.colorTextValue, 'lumon:body-s')} data-test-id={testId('selectedColor')}>
              {styleNamePrefix}
              {selectedStyle.specs || selectedStyle.color}
            </span>
          </div>

          {totalStyleCount > COLOR_TWISTER_COLUMN_COUNT && (
            <ColorTwister.Toggle
              className="sm:hidden"
              itemCount={totalStyleCount}
              onToggleChange={action => {
                track(() => [
                  evColorTwister,
                  {
                    action: action,
                    styleId: selectedStyle.styleId,
                    visibleStyles: visibleStyleCount,
                    totalStyles: totalStyleCount
                  }
                ]);
              }}
            />
          )}
        </div>

        <div className="mb-2">{sizingChooser}</div>

        <fieldset className="-mx-5">
          <legend className="sr-only">Select A {styleLabel}</legend>

          {isGiftCard ? (
            <select
              id="pdp-color-select"
              name="styleId"
              className={css.dropdown}
              data-test-id={testId('pdp-color')}
              onChange={onColorChangeForDropdown}
              value={selectedStyleId}
            >
              {styleList.map(({ styleId, color, isNew }) => (
                <option data-color-name={color} data-style-id={styleId} data-is-new={isNew} key={styleId} value={styleId}>
                  {styleNamePrefix}
                  {color}
                </option>
              ))}
            </select>
          ) : (
            <>
              <ColorTwister.Items className="px-5 sm:hidden" columnCount={COLOR_TWISTER_COLUMN_COUNT}>
                {styleList.map(style => (
                  <ColorTwister.Item key={style.styleId} shouldScroll={style.styleId === selectedStyleId}>
                    <Swatch
                      {...props}
                      airplaneCache={airplaneCache}
                      style={style}
                      onColorChangeForSwatches={onColorChangeForSwatches}
                      pageType={pageType}
                    />
                  </ColorTwister.Item>
                ))}
              </ColorTwister.Items>

              <div className="hidden px-5 sm:block">
                <div className={css.swatches} data-test-id={testId('styleSwatchContainer')}>
                  {styleList.map(style => (
                    <Swatch
                      key={style.styleId}
                      {...props}
                      airplaneCache={airplaneCache}
                      style={style}
                      onColorChangeForSwatches={onColorChangeForSwatches}
                      pageType={pageType}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </fieldset>

        {collapsable && totalStyleCount > INITIAL_COLLAPSED_STYLES && (
          <button className={css.collapseLink} type="button" onClick={toggleCollapse}>
            See {collapsed ? 'more' : 'less'} {isGiftCard ? 'options' : 'colors'}
          </button>
        )}
      </div>
    </ColorTwister.Root>
  );
}

export function Swatch({
  airplaneCache,
  style,
  onColorChangeForSwatches,
  pageType,
  ...props
}: {
  airplaneCache?: AirplaneCache;
  style: ProductStyle;
  onColorChangeForSwatches: OnChange<HTMLInputElement>;
  pageType: string;
  airplaneAllowList: string[];
  alwaysAllowAirplane: boolean;
  productState: ProductDetailState;
  productType: string;
  selectedStyle: ProductStyle;
  shouldCheckStock?: boolean;
}) {
  const { airplaneAllowList, alwaysAllowAirplane, productState, productType, selectedStyle, shouldCheckStock = true } = props;

  const { detail: { brandName, productId } = {} as FormattedProductBundle } = productState;
  const { colorId, styleId } = style;

  let isInStock;
  if (shouldCheckStock) {
    if (showAirplaneSizing(airplaneAllowList, alwaysAllowAirplane, productType, airplaneCache)) {
      isInStock = airplaneCache.available.colorOptions.includes(colorId);
    } else {
      isInStock = !isColorOosForSelectedSizing(colorId, productState);
    }
  }

  return (
    <SwankyStyleSwatch
      key={styleId}
      isSelected={styleId === selectedStyle.styleId}
      isUnavailable={shouldCheckStock && !isInStock}
      onColorChange={onColorChangeForSwatches}
      productType={productType}
      style={style}
      brandName={brandName}
      namespace={`${productId}-${pageType}`}
    />
  );
}

function mapStateToProps(store: AppState) {
  const {
    localStorage: {
      debugFlags: { alwaysAllowAirplane }
    },
    killswitch: { airplaneAllowList },
    product: { colorId, detail: { sizing } = {} }
  } = store;
  return { airplaneAllowList, alwaysAllowAirplane, colorId, sizing };
}

export default connect(mapStateToProps)(SwankySwatchBoxTreatment);
